class DobInputs extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const locale = this.getAttribute('locale') || '';
    const isUS = locale.toLowerCase() === 'en-us';

    const tpl = this.querySelector('template');
    if (tpl && tpl.content) {
      this.appendChild(tpl.content.cloneNode(true));
    }

    const day = this.querySelector('[data-dob-day]');
    const month = this.querySelector('[data-dob-month]');
    const year = this.querySelector('[data-dob-year]');
    const hidden = this.querySelector('[data-dob-hidden]');
    const errorEl = this.querySelector('[data-dob-error]');

    if (!day || !month || !year || !hidden || !errorEl) return;

    if (isUS) {
      const children = Array.from(this.children);
      const [dayEl, slash1, monthEl, slash2, yearEl] = children;

      this.innerHTML = '';
      this.append(monthEl, slash1, dayEl, slash2, yearEl);
    }

    const messages = {
      incomplete: this.dataset.errorIncomplete || '',
      invalid: this.dataset.errorInvalid || '',
      future: this.dataset.errorFuture || '',
      minimumAge: this.dataset.errorMinimumAge || '',
      unrealistic: this.dataset.errorUnrealistic || '',
    };

    const numericOnly = (el) => {
      el.value = el.value.replace(/\D/g, '');
    };

    const advanceFocus = (currentEl, nextEl) => {
      if (currentEl.value.length === currentEl.maxLength) nextEl?.focus();
    };

    const clamp = (value, min, max) => {
      if (Number.isNaN(value)) return '';
      return Math.min(Math.max(value, min), max);
    };

    const pad2 = (val) => String(val).padStart(2, '0');

    const isComplete = () =>
      day.value.length === 2 && month.value.length === 2 && year.value.length === 4;

    const showError = (message = '') => {
      if (message) {
        errorEl.textContent = message;
        errorEl.style.display = 'block';
      } else {
        errorEl.textContent = '';
        errorEl.style.display = 'none';
      }
    };

    const clearHidden = () => {
      hidden.value = '';
    };

    const validateIfComplete = () => {
      if (isComplete()) {
        validateDOB();
      } else {
        clearHidden();
        showError();
      }
    };

    const handleBlur = () => {
      if (day.value.length === 1) {
        day.value = pad2(clamp(parseInt(day.value, 10), 1, 31));
      }
      if (month.value.length === 1) {
        month.value = pad2(clamp(parseInt(month.value, 10), 1, 12));
      }
      validateIfComplete();
    };

    const anyFilled = () => day.value || month.value || year.value;

    const onFocusIn = (event) => {
      if (event.target === day || event.target === month || event.target === year) {
        showError();
      }
    };

    const onFocusOut = (event) => {
      const next = event.relatedTarget;
      if (!next || !this.contains(next)) {
        if (anyFilled() && !isComplete()) {
          showError(messages.incomplete);
        }
      }
    };

    this.addEventListener('focusin', onFocusIn);
    this.addEventListener('focusout', onFocusOut);

    day.addEventListener('input', () => {
      numericOnly(day);
      clearHidden();
      if (day.value.length === 2) {
        day.value = pad2(clamp(parseInt(day.value, 10), 1, 31));

        advanceFocus(day, isUS ? year : month);
        validateIfComplete();
      }
    });
    day.addEventListener('blur', handleBlur);

    month.addEventListener('input', () => {
      numericOnly(month);
      clearHidden();
      if (month.value.length === 2) {
        month.value = pad2(clamp(parseInt(month.value, 10), 1, 12));

        advanceFocus(month, isUS ? day : year);
        validateIfComplete();
      }
    });
    month.addEventListener('blur', handleBlur);

    year.addEventListener('input', () => {
      numericOnly(year);
      clearHidden();
      validateIfComplete();
    });
    year.addEventListener('blur', handleBlur);

    const validateDOB = () => {
      const d = parseInt(day.value, 10);
      const m = parseInt(month.value, 10) - 1;
      const y = parseInt(year.value, 10);

      const dob = new Date(y, m, d);
      const today = new Date();

      if (dob.getFullYear() !== y || dob.getMonth() !== m || dob.getDate() !== d) {
        clearHidden();
        showError(messages.invalid);
        return false;
      }

      if (dob > today) {
        clearHidden();
        showError(messages.future);
        return false;
      }

      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }

      if (age < 18) {
        clearHidden();
        showError(messages.minimumAge);
        return false;
      }

      if (age > 100) {
        clearHidden();
        showError(messages.unrealistic);
        return false;
      }

      const format = hidden.dataset.format;
      if (format) {
        hidden.value = format.replace('YYYY', y).replace('MM', pad2(m + 1)).replace('DD', pad2(d));
      } else {
        hidden.value = `${y}-${pad2(m + 1)}-${pad2(d)}`;
      }
      showError();
      return true;
    };

    const prefillFromValue = (value) => {
      if (!value) return;
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
      if (!match) return;

      const [, yStr, mStr, dStr] = match;
      const y = parseInt(yStr, 10);
      const m = parseInt(mStr, 10);
      const d = parseInt(dStr, 10);

      if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return;

      day.value = pad2(d);
      month.value = pad2(m);
      year.value = String(y);

      validateDOB();
    };

    const initialValue = this.getAttribute('value');
    if (initialValue) {
      prefillFromValue(initialValue);
    }
  }
}

if (!customElements.get('dob-inputs')) {
  customElements.define('dob-inputs', DobInputs);
}
